/**
 * Service Worker registration and management
 */

interface ServiceWorkerConfig {
  onUpdate?: (registration: ServiceWorkerRegistration) => void;
  onSuccess?: (registration: ServiceWorkerRegistration) => void;
  onError?: (error: Error) => void;
}

class ServiceWorkerManager {
  private registration: ServiceWorkerRegistration | null = null;
  private config: ServiceWorkerConfig = {};

  /**
   * Register service worker
   */
  async register(config: ServiceWorkerConfig = {}): Promise<boolean> {
    this.config = config;

    if (!('serviceWorker' in navigator)) {
      console.log('Service Worker not supported');
      return false;
    }

    try {
      const registration = await navigator.serviceWorker.register('/sw.js');
      this.registration = registration;

      console.log('Service Worker registered successfully');

      // Handle updates
      registration.addEventListener('updatefound', () => {
        this.handleUpdateFound(registration);
      });

      // Check for existing waiting worker
      if (registration.waiting) {
        this.config.onUpdate?.(registration);
      }

      this.config.onSuccess?.(registration);
      return true;

    } catch (error) {
      console.error('Service Worker registration failed:', error);
      this.config.onError?.(error as Error);
      return false;
    }
  }

  /**
   * Handle service worker update
   */
  private handleUpdateFound(registration: ServiceWorkerRegistration) {
    const newWorker = registration.installing;
    if (!newWorker) return;

    newWorker.addEventListener('statechange', () => {
      if (newWorker.state === 'installed') {
        if (navigator.serviceWorker.controller) {
          // New content is available
          console.log('New content available, please refresh');
          this.config.onUpdate?.(registration);
        } else {
          // Content is cached for offline use
          console.log('Content cached for offline use');
          this.config.onSuccess?.(registration);
        }
      }
    });
  }

  /**
   * Update service worker
   */
  async update(): Promise<void> {
    if (!this.registration) {
      throw new Error('Service Worker not registered');
    }

    await this.registration.update();
  }

  /**
   * Skip waiting and activate new service worker
   */
  skipWaiting(): void {
    if (this.registration?.waiting) {
      this.registration.waiting.postMessage({ type: 'SKIP_WAITING' });
    }
  }

  /**
   * Get cache statistics
   */
  async getCacheStats(): Promise<Record<string, number>> {
    if (!this.registration?.active) {
      throw new Error('Service Worker not active');
    }

    return new Promise((resolve, reject) => {
      const messageChannel = new MessageChannel();
      
      messageChannel.port1.onmessage = (event) => {
        if (event.data.error) {
          reject(new Error(event.data.error));
        } else {
          resolve(event.data);
        }
      };

      this.registration!.active!.postMessage(
        { type: 'GET_CACHE_STATS' },
        [messageChannel.port2]
      );
    });
  }

  /**
   * Clear all caches
   */
  async clearCaches(): Promise<void> {
    if (!this.registration?.active) {
      throw new Error('Service Worker not active');
    }

    return new Promise((resolve, reject) => {
      const messageChannel = new MessageChannel();
      
      messageChannel.port1.onmessage = (event) => {
        if (event.data.error) {
          reject(new Error(event.data.error));
        } else {
          resolve();
        }
      };

      this.registration!.active!.postMessage(
        { type: 'CLEAR_CACHE' },
        [messageChannel.port2]
      );
    });
  }

  /**
   * Unregister service worker
   */
  async unregister(): Promise<boolean> {
    if (!this.registration) {
      return false;
    }

    const result = await this.registration.unregister();
    this.registration = null;
    return result;
  }

  /**
   * Check if service worker is supported
   */
  static isSupported(): boolean {
    return 'serviceWorker' in navigator;
  }

  /**
   * Get registration
   */
  getRegistration(): ServiceWorkerRegistration | null {
    return this.registration;
  }
}

// Export singleton instance
export const serviceWorkerManager = new ServiceWorkerManager();

/**
 * React hook for service worker management
 */
export function useServiceWorker(config: ServiceWorkerConfig = {}) {
  const [isRegistered, setIsRegistered] = React.useState(false);
  const [isUpdateAvailable, setIsUpdateAvailable] = React.useState(false);
  const [cacheStats, setCacheStats] = React.useState<Record<string, number>>({});

  React.useEffect(() => {
    const enhancedConfig: ServiceWorkerConfig = {
      ...config,
      onSuccess: (registration) => {
        setIsRegistered(true);
        config.onSuccess?.(registration);
      },
      onUpdate: (registration) => {
        setIsUpdateAvailable(true);
        config.onUpdate?.(registration);
      },
    };

    serviceWorkerManager.register(enhancedConfig);
  }, []);

  const updateServiceWorker = React.useCallback(() => {
    serviceWorkerManager.skipWaiting();
    setIsUpdateAvailable(false);
    // Reload page to use new service worker
    window.location.reload();
  }, []);

  const refreshCacheStats = React.useCallback(async () => {
    try {
      const stats = await serviceWorkerManager.getCacheStats();
      setCacheStats(stats);
    } catch (error) {
      console.error('Failed to get cache stats:', error);
    }
  }, []);

  const clearCaches = React.useCallback(async () => {
    try {
      await serviceWorkerManager.clearCaches();
      setCacheStats({});
      console.log('Caches cleared successfully');
    } catch (error) {
      console.error('Failed to clear caches:', error);
    }
  }, []);

  return {
    isRegistered,
    isUpdateAvailable,
    cacheStats,
    updateServiceWorker,
    refreshCacheStats,
    clearCaches,
  };
}

/**
 * Performance monitoring for service worker cache hits/misses
 */
export function setupServiceWorkerPerformanceMonitoring() {
  if (!ServiceWorkerManager.isSupported()) {
    return;
  }

  navigator.serviceWorker.addEventListener('message', (event) => {
    if (event.data?.type === 'CACHE_PERFORMANCE') {
      const { url, fromCache, timestamp } = event.data.data;
      
      // Record cache performance metrics
      if (window.performance && window.performance.mark) {
        const metricName = fromCache ? 'cache_hit' : 'cache_miss';
        window.performance.mark(`sw_${metricName}_${timestamp}`);
        
        // Send to analytics
        if ('fetch' in window) {
          fetch('/api/analytics/performance', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              name: metricName,
              value: 1,
              rating: fromCache ? 'good' : 'needs-improvement',
              timestamp,
              metadata: { url },
            }),
          }).catch(() => {
            // Silently fail
          });
        }
      }
    }
  });
}

// Auto-setup performance monitoring
if (typeof window !== 'undefined') {
  setupServiceWorkerPerformanceMonitoring();
}

export { ServiceWorkerManager };